import {
  Avatar,
  Button,
  Form,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckOutlined, CloseOutlined, EditOutlined, GlobalOutlined, LockOutlined,
} from '@ant-design/icons';
import React, { useState } from 'react';
import {
  bool, func, number, string,
} from 'prop-types';
import classNames from 'classnames';
import I18n from 'i18n-js';
import CommentEditor from './CommentEditor';
import MarkdownPreview from './MarkdownPreview';

const { Text } = Typography;

const CommentItem = ({
  id = null,
  ticketId = null,
  authorName = '',
  avatarUrl = '',
  content = '',
  createdAt = '',
  canEdit = false,
  internal = false,
  canSetInternalComment = false,
  onUpdate = null,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const editingInternal = Form.useWatch('internal', form);
  const editingContent = Form.useWatch('content', form);
  const editFormClassName = classNames('ticket-comments-form__editor', {
    'ticket-comments-form__editor--internal': editingInternal,
  });

  const handleSave = async () => {
    const values = await form.validateFields();
    setIsSaving(true);
    try {
      await onUpdate(id, values);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.setFieldsValue({ content, internal });
  };

  const handleStartEditing = () => {
    form.setFieldsValue({ content, internal });
    setIsEditing(true);
  };

  return (
    <div className={classNames('ticket-comment', { 'ticket-comment--internal': internal })}>
      <Avatar src={avatarUrl} size={36} className="ticket-comment__avatar" />

      <div className="ticket-comment__body">
        <div className="ticket-comment__header">
          <Text strong className="ticket-comment__author">{authorName}</Text>
          <Text type="secondary" className="ticket-comment__date">{createdAt}</Text>
          {internal && (
            <Tag color="orange" icon={<LockOutlined />} className="ticket-comment__internal-tag">
              {I18n.t('ticket_comments.visibility.internal')}
            </Tag>
          )}
          {canEdit && !isEditing && (
            <Tooltip title={I18n.t('helpers.submit.ticket_comment.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                className="ticket-comment__edit-btn"
                aria-label={I18n.t('helpers.submit.ticket_comment.edit')}
                onClick={handleStartEditing}
              />
            </Tooltip>
          )}
        </div>

        {isEditing ? (
          <div className={classNames(editFormClassName, 'ticket-comment__edit-form')}>
            <Form form={form} initialValues={{ content, internal }}>
              <Form.Item
                name="content"
                className="ticket-comments-form__content-field"
                getValueFromEvent={(value) => value}
              >
                <CommentEditor
                  ticketId={ticketId}
                  height={140}
                />
              </Form.Item>

              <div className="ticket-comments-form__footer">
                {canSetInternalComment ? (
                  <Form.Item name="internal" className="ticket-comments-form__visibility">
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      size="small"
                      className="ticket-comments-form__visibility-toggle"
                      options={[
                        {
                          value: false,
                          label: (
                            <span className="ticket-comments-form__visibility-option">
                              <GlobalOutlined />
                              {I18n.t('ticket_comments.visibility.public')}
                            </span>
                          ),
                        },
                        {
                          value: true,
                          label: (
                            <span className="ticket-comments-form__visibility-option">
                              <LockOutlined />
                              {I18n.t('ticket_comments.visibility.internal')}
                            </span>
                          ),
                        },
                      ]}
                    />
                  </Form.Item>
                ) : <div />}
                <Space>
                  <Tooltip title={I18n.t('helpers.submit.ticket_comment.cancel')}>
                    <Button
                      size="small"
                      shape="circle"
                      icon={<CloseOutlined />}
                      disabled={isSaving}
                      onClick={handleCancel}
                      aria-label={I18n.t('helpers.submit.ticket_comment.cancel')}
                    />
                  </Tooltip>
                  <Tooltip title={I18n.t('helpers.submit.ticket_comment.update')}>
                    <Button
                      type="primary"
                      size="small"
                      shape="circle"
                      icon={<CheckOutlined />}
                      loading={isSaving}
                      disabled={!editingContent?.trim()}
                      onClick={handleSave}
                      aria-label={I18n.t('helpers.submit.ticket_comment.update')}
                    />
                  </Tooltip>
                </Space>
              </div>
            </Form>
          </div>
        ) : (
          <MarkdownPreview content={content} />
        )}
      </div>
    </div>
  );
};

CommentItem.propTypes = {
  id: number,
  ticketId: number,
  authorName: string,
  avatarUrl: string,
  content: string,
  createdAt: string,
  canEdit: bool,
  internal: bool,
  canSetInternalComment: bool,
  onUpdate: func,
};

export default CommentItem;
